/**
 * Shared types for tooth components
 */

export type ToothView =
  | 'buccal'
  | 'palatal'
  | 'lingual'
  | 'crown-top'
  | 'crown-bottom'

export type ToothType = 'molar' | 'premolar' | 'canine' | 'incisor'

export type AttritionSeverity = 'slight' | 'moderate' | 'severe'
export type AbrasionSeverity = 'slight' | 'moderate' | 'severe'
export type CariesType = 'D1' | 'D2' | 'D3' | 'E1' | 'E2'
export type FurcationType = 'C1' | 'C2' | 'C3' | 'c1' | 'c2' | 'c3'
export type RecessionClass = 'class1' | 'class2' | 'class3' | 'class4'
export type CrownMaterial = 'zirconia' | 'pfm' | 'pfz' | 'metal' | 'ceramic'
export type InlayMaterial = 'gold' | 'zirconia' | 'ceramic'
export type OnlayMaterial = 'gold' | 'zirconia' | 'ceramic'
export type FillingMaterial =
  | 'ceramic'
  | 'composite'
  | 'gold'
  | 'amalgam'
  | 'temporary'
export type VeneerMaterial = 'ceramic' | 'composite' | 'zirconia'
export type ImpactedDirection = 'left' | 'right' | 'down' | 'up'
export type BridgeType =
  | boolean
  | 'left_bridge'
  | 'left_abutment'
  | 'right_bridge'
  | 'right_abutment'
  | 'right_left_bridge'
  | 'right_left_abutment'
export type BridgeSource = 'bridge' | 'abutment'
export type PeriradicularSeverity = 'slight' | 'moderate' | 'severe'

export type FractureType =
  | 'mesiobuccal'
  | 'distobuccal'
  | 'mesiolingual'
  | 'distolingual'
  | 'mesiopalatal'
  | 'distopalatal'
  | 'incisal'
  | 'difl'
  | 'difp'
  | 'mifl'
  | 'mifp'
  | 'vertical_root'
  | 'horizontal_root'

export type CrackType =
  | 'vertical_crown_buccal'
  | 'vertical_crown_lingual'
  | 'vertical_crown_palatal'
  | 'vertical_crown_occlusal'
  | 'horizontal_crown_buccal'
  | 'horizontal_crown_lingual'
  | 'horizontal_crown_palatal'
  | 'horizontal_crown_occlusal'

/**
 * Grouped condition types for better organization
 */
export interface SimpleConditions {
  missing?: boolean
  implant?: boolean
  rootCanal?: boolean
  pulpotomy?: boolean
  pulpectomy?: boolean
  bleeding?: boolean
  retainer?: boolean
  supernumerary?: boolean
  sinusTract?: boolean
  apicoetomy?: boolean
  periapical?: boolean
  veneer?: boolean
  veneerMaterial?: VeneerMaterial
  sealants?: boolean
  retainedPrimary?: boolean
}

export interface ErosionConditions {
  general?: boolean
  buccal?: boolean
  palatal?: boolean
  lingual?: boolean
  occlusal?: boolean
}

export interface AbrasionConditions {
  severity?: AbrasionSeverity
  buccal?: boolean
  palatal?: boolean
  lingual?: boolean
}

export const resolveAbrasionSeverityForView = (
  abrasion: AbrasionConditions | undefined,
  view: ToothView,
): AbrasionSeverity | undefined => {
  if (!abrasion?.severity) {
    return undefined
  }
  const hasSpecificSurface = Boolean(
    abrasion.buccal || abrasion.palatal || abrasion.lingual,
  )
  if (!hasSpecificSurface) {
    return undefined
  }
  if (view === 'buccal') {
    return abrasion.buccal ? abrasion.severity : undefined
  }
  if (view === 'palatal') {
    return abrasion.palatal ? abrasion.severity : undefined
  }
  if (view === 'lingual') {
    return abrasion.lingual ? abrasion.severity : undefined
  }
  return undefined
}

export interface CariesConditions {
  surfaces?: string[]
  type?: CariesType
  recurrent?: boolean
}

export interface InlayConditions {
  surfaces?: string[]
  material?: InlayMaterial
}

export interface OnlayConditions {
  surfaces?: string[]
  material?: OnlayMaterial
}

export interface FillingConditions {
  surfaces?: string[]
  material?: FillingMaterial
}

export interface PeriradicularConditions {
  present?: boolean
  severity?: PeriradicularSeverity
}

export interface FurcationConditions {
  level?: FurcationType
  areas?: string
}

export interface BridgeConditions {
  connector?: BridgeType
  source?: BridgeSource
  material?: CrownMaterial
}

export interface FractureConditions {
  types?: FractureType | FractureType[]
  recurrent?: boolean
}

export interface CrownConditions {
  material?: CrownMaterial
}

export interface RecessionConditions {
  type?: RecessionClass
}

export interface AttritionConditions {
  severity?: AttritionSeverity
}

export interface ImpactedConditions {
  direction?: ImpactedDirection
}

export interface DriftedConditions {
  surfaces?: string[]
}

export interface CrackConditions {
  types?: CrackType | CrackType[]
  recurrent?: boolean
}

/** Props for BaseTooth (grouped condition structure). */
export interface BaseToothProps {
  toothNumber: number
  view: ToothView
  conditions?: SimpleConditions
  erosion?: ErosionConditions
  furcation?: FurcationConditions
  recession?: RecessionConditions
  crown?: CrownConditions
  caries?: CariesConditions
  recurringCaries?: CariesConditions
  inlay?: InlayConditions
  onlay?: OnlayConditions
  filling?: FillingConditions
  impacted?: ImpactedConditions
  drifted?: DriftedConditions
  bridge?: BridgeConditions
  attrition?: AttritionConditions
  abrasion?: AbrasionConditions
  periradicular?: PeriradicularConditions
  crack?: CrackConditions
  fracture?: FractureConditions
  showMissingOverlay?: boolean
  showRetainedPrimaryOverlay?: boolean
}

/** Factory / view-model props (BaseTooth without toothNumber/view). */
export type ToothComponentProps = Omit<BaseToothProps, 'toothNumber' | 'view'>

/** Class map type for surface mappings. */
export type ClassMap = Record<string, string | string[]>

/** Configuration for a specific tooth. */
export interface ToothConfig {
  cariesClassMap: ClassMap
  inlayClassMap: ClassMap
  onlayClassMap: ClassMap
  fillingClassMap: ClassMap
  driftedClassMap: ClassMap
}
